/**
 * Unified personnel — the pure layer.
 *
 * docs/UNIFIED_PERSONNEL_MANAGEMENT.md is the spec. The page shows ONE
 * population: every Person with a membership in this clinic, whatever kind of
 * membership that is. Two very different wire shapes feed it —
 *
 *   practitioner → `listClinicDoctors` (doctorpreference link + accounting
 *                  contract + scheduling presences, joined by the clinic BFF)
 *   staff        → `listStaffMembers`  (clinicmanagement + Keycloak)
 *
 * — and everything below exists to turn those two into one row shape so the
 * table, the search box and the filters never branch on kind. Kind stays a
 * DATA field on the row (§7.7 of the spec: a future third kind must be a new
 * badge value, not a new code path).
 *
 * Deliberately free of React and of `t`: this is what the unit tests drive, and
 * display fallbacks («دکتر ۱۲۳۴۵۶») belong to the component that has the
 * catalogue. A row therefore carries `displayName: ''` when the name has not
 * loaded yet rather than a baked-in placeholder.
 *
 * The one import is the kit's digit fold. Numerals are a PLATFORM rule — the same
 * three keyboards feed every screen — and this module has no business keeping its
 * own table of them; see `normText`.
 */

import { phone as phoneLib } from '@parsina/framework';

/** The two kinds of membership this page manages. Patients are not one (spec §3.1). */
export const KIND = {
  PRACTITIONER: 'practitioner',
  STAFF: 'staff',
};

/**
 * One status vocabulary across both kinds.
 *
 * The two services disagree on the wire: staff are `ACTIVE`/`SUSPENDED`, a
 * practitioner link is `accepted` or anything else (it ships `pending`, and the
 * service-side migration adds `invited`/`requested`). Collapsing them here is
 * what lets one «وضعیت» filter drive both.
 */
export const PERSON_STATUS = {
  ACTIVE: 'ACTIVE',
  PENDING: 'PENDING',
  SUSPENDED: 'SUSPENDED',
  // Not the same as SUSPENDED, and the difference is whose decision it was:
  // suspended means the clinic took access away, declined means the PERSON said
  // no. Collapsing them tells an admin their colleague was suspended by someone
  // at the clinic when in fact the invitation was turned down.
  DECLINED: 'DECLINED',
};

/** Pending first — it is the only status with work attached to it (spec §5.1). */
const STATUS_ORDER = {
  [PERSON_STATUS.PENDING]: 0,
  [PERSON_STATUS.ACTIVE]: 1,
  [PERSON_STATUS.SUSPENDED]: 2,
  // Last: nothing is owed to a declined invitation, but it must still be
  // findable — an admin who cannot see it re-invites into the same refusal.
  [PERSON_STATUS.DECLINED]: 3,
};

/**
 * What stops this person from working here today
 * (docs/PEOPLE_PAGE_REDESIGN.md §5.4).
 *
 * The page's central claim is that it answers «can this person work here?»,
 * not merely «who is here». Three of these are silent failures the old page
 * could only reveal by opening a drawer and clicking a tab: a practitioner
 * nobody can book (no shifts) or bill (no services — a visit price and every
 * share are terms of the services they provide; there is no separate financial
 * contract since 2026-09-14), and a member who can sign in and then see nothing
 * at all (no roles). They are computed here so the table, the KPI, the filter
 * and the drawer all read one definition.
 */
export const ISSUE = {
  NO_SHIFTS: 'NO_SHIFTS',
  NO_SERVICES: 'NO_SERVICES',
  NO_ROLES: 'NO_ROLES',
  SYNC_ERROR: 'SYNC_ERROR',
  PROFILE_UNAVAILABLE: 'PROFILE_UNAVAILABLE',
};

/**
 * clinic_staff.status → the page's shared vocabulary.
 *
 * REJECTED IS HERE NOW, and it was not before because the state was unreachable:
 * `rejectClinicInvitation` wrote `rejected` to a service that rejected the word,
 * so no roster ever carried one. It does now, `listStaffMembers` does NOT filter
 * it out, and an unmapped status fell through `statusLabel` to «معلق» — telling
 * an admin the clinic had suspended a colleague who had in fact declined.
 *
 * Keeping the row visible is the documented decision (`rejectClinicInvitation`
 * in the clinic BFF): a rejection is a status and never a delete, precisely so
 * «not answered yet» and «declined» do not look identical on this page.
 */
const STAFF_STATUS_MAP = {
  SUSPENDED: PERSON_STATUS.SUSPENDED,
  INVITED: PERSON_STATUS.PENDING,
  ACTIVE: PERSON_STATUS.ACTIVE,
  REJECTED: PERSON_STATUS.DECLINED,
};

/** The three sort orders the toolbar offers. */
export const SORT = {
  STATUS: 'status',
  NAME: 'name',
  JOINED: 'joined',
};

/**
 * Fold a string to something two humans typing the same name would agree on.
 *
 * A receptionist searching «کریمی» must find a row stored with the Arabic kaf,
 * and «۰۹۱۲» must find a phone stored in Latin digits — the search box accepts
 * whatever the keyboard produces. Same normalisation the delete-confirmation
 * guard uses, so "type the name to confirm" cannot reject a correct name over a
 * codepoint.
 *
 * TWO FOLDS, and only one of them is this module's business.
 *
 *   - DIGITS are the kit's, `phone.foldDigits`. The rule is a platform rule, not a
 *     personnel rule, and the local copy that used to stand here carried only the
 *     Arabic-Indic and Persian blocks — it missed the FULLWIDTH digits (U+FF10–19)
 *     that some IME keyboards emit, which the kit, `PhoneNumbers.java#foldDigits`
 *     and `app/domain/digits.py#DIGIT_FOLD` all carry. A search box that silently
 *     disagrees with the canonicaliser about what «۰۹۱۲» is has no way to be right.
 *   - LETTERS stay here. ک/ی vs ك/ي is about NAMES, and nothing in the phone module
 *     knows or should know about Arabic orthography.
 */
export const normText = (value) => {
  let out = phoneLib.foldDigits(String(value ?? ''));
  // i18n-exempt: the CHARACTERS are being folded, not copy — an Arabic keyboard
  // and a Persian one produce different codepoints for the same letter, and a
  // search or a typed confirmation must not care which one was used.
  out = out.replace(/ي/g, 'ی').replace(/ك/g, 'ک');
  // ZWNJ and runs of whitespace both collapse: «علی‌رضا» and «علی رضا» are one name.
  return out.replace(/[‌\s]+/g, ' ').trim().toLowerCase();
};

/**
 * A practitioner membership row → the unified shape.
 *
 * `info` is attached asynchronously by useClinicDoctors (a per-row profile
 * lookup), so every field it carries has to survive being absent on the first
 * render — the row is real from the moment the link loads, name or no name.
 */
export const practitionerToPerson = (link) => {
  const info = link.info || null;
  const status = link.status === 'accepted' ? PERSON_STATUS.ACTIVE : PERSON_STATUS.PENDING;
  return {
    key: `p:${link.id}`,
    kind: KIND.PRACTITIONER,
    id: link.id,                       // membership id — what the mutations take
    personId: link.doctorId || '',     // Keycloak sub — the platform identity
    displayName: (info?.name || link.doctorName || '').trim(),
    phone: info?.phone || '',
    status,
    joinedAt: link.joinedAt || '',
    registeredVia: link.registeredVia || '',
    syncStatus: null,                  // Keycloak sync is a staff-only concern
    // Identity (read-only on this page — owned by the person, not the clinic)
    specialty: info?.specialty || '',
    medicalCode: info?.medicalCode || '',
    bio: info?.bio || '',
    email: info?.email || '',
    nationalId: info?.nationalId || '',
    infoLoaded: !!info,
    // Membership (clinic-managed)
    visitDurationMinutes: link.visitDurationMinutes ?? null,
    // تأمین's SIAM id for this practitioner here (e-prescribing only).
    siamId: link.siamId || '',
    // The clinic's grant to book from the doctor app. false (default) = books
    // directly; true = their bookings wait in «درخواست‌های نوبت».
    bookingRequiresApproval: !!link.bookingRequiresApproval,
    // Owned elsewhere, mirrored read-only: accounting / scheduling. `serviceCount`
    // null means «could not be read», which is not «provides nothing».
    serviceCount: link.serviceCount ?? null,
    presences: link.presences || [],
    // Staff-only fields, present so callers never have to check kind first
    tier: null,
    roles: [],
    extraPermissions: [],
    effectivePermissions: [],
    raw: link,
  };
};

/** A staff member row → the unified shape. */
export const staffToPerson = (member) => ({
  key: `s:${member.id}`,
  kind: KIND.STAFF,
  id: member.id,
  personId: member.userId || '',
  displayName: (member.displayName || '').trim(),
  phone: member.phone || '',
  // INVITED is a real staff status now, not just a practitioner one: adding a
  // person writes the row `invited` and grants nothing until they accept
  // (docs/CLINIC_MULTI_CLINIC_UX_REV2.md §6.7). It folds onto PENDING so the one
  // «وضعیت» filter, the pending-first sort and the KPI keep working unchanged —
  // the page's own rule is that a new state is a value, never a new code path.
  status: STAFF_STATUS_MAP[member.status] || PERSON_STATUS.ACTIVE,
  joinedAt: member.joinedAt || '',
  registeredVia: '',
  syncStatus: member.syncStatus || null,
  specialty: '',
  medicalCode: '',
  bio: '',
  email: '',
  nationalId: '',
  infoLoaded: true,
  visitDurationMinutes: null,
  siamId: '',
  bookingRequiresApproval: false,
  serviceCount: null,
  // The staff member's own weekly shifts (PM-0468 §2.2) — the same rows a
  // doctor has, keyed on the person. Empty = none declared.
  presences: member.presences || [],
  tier: member.tier || 'MEMBER',
  roles: member.roles || [],
  extraPermissions: member.extraPermissions || [],
  effectivePermissions: member.effectivePermissions || [],
  raw: member,
});

/**
 * A staff JOIN REQUEST → the unified shape.
 *
 * This is the row the redesign adds (§6.2). A join request is not a membership:
 * somebody signed in, opened `/invite/<code>` and asked to be let in, and
 * `clinic_staff` has no row for them yet. The old page therefore showed them in
 * a card stacked above the table, which meant the page's own «one population,
 * one table» rule was only half true and the same waiting person could be read
 * as two different kinds of thing depending on which half you looked at.
 *
 * Rendering them as PENDING rows of kind «پرسنل» costs one normaliser and buys
 * one table, one status vocabulary, one sort and one filter. `requestId` is
 * what the approve/reject mutations take; `id` carries it too so callers that
 * only ever pass `person.id` keep working.
 */
export const requestToPerson = (request) => ({
  key: `r:${request.id}`,
  kind: KIND.STAFF,
  id: request.id,
  requestId: request.id,
  personId: request.userId || '',
  displayName: (request.displayName || '').trim(),
  phone: request.phone || '',
  status: PERSON_STATUS.PENDING,
  joinedAt: '',
  requestedAt: request.requestedAt || '',
  registeredVia: 'qr_invitation',
  syncStatus: null,
  specialty: '',
  medicalCode: '',
  bio: '',
  email: '',
  nationalId: '',
  infoLoaded: true,
  visitDurationMinutes: null,
  siamId: '',
  bookingRequiresApproval: false,
  serviceCount: null,
  presences: [],
  tier: 'MEMBER',
  roles: [],
  extraPermissions: [],
  effectivePermissions: [],
  raw: request,
});

/** True for a row that is a join request rather than a membership. */
export const isJoinRequest = (person) => !!person?.requestId;

/**
 * What is missing before this person can do their job (§5.4).
 *
 * Only ACTIVE rows are examined. A pending row's only outstanding item is the
 * decision itself, which the status badge already says louder than a chip
 * could; listing «بدون شیفت» beside «در انتظار تأیید» would read as two
 * problems where there is one.
 */
export const issuesFor = (person) => {
  if (!person || person.status !== PERSON_STATUS.ACTIVE) return [];
  const out = [];
  if (person.kind === KIND.PRACTITIONER) {
    if ((person.presences || []).length === 0) out.push(ISSUE.NO_SHIFTS);
    if (person.serviceCount === 0) out.push(ISSUE.NO_SERVICES);
    if (!person.infoLoaded) out.push(ISSUE.PROFILE_UNAVAILABLE);
  } else {
    if (person.tier === 'MEMBER'
      && (person.roles || []).length === 0
      && (person.extraPermissions || []).length === 0) out.push(ISSUE.NO_ROLES);
    if (person.syncStatus === 'ERROR') out.push(ISSUE.SYNC_ERROR);
  }
  return out;
};

/** Issues that mean somebody has to do something. A missing profile is not one. */
export const actionableIssues = (person) =>
  issuesFor(person).filter((i) => i !== ISSUE.PROFILE_UNAVAILABLE);

/** Whether a staff row carries clinic-wide authority (admin tier, or the founder). */
const holdsAdminPower = (person) => person?.kind === KIND.STAFF
  && (person.tier === 'ADMIN' || person.tier === 'OWNER' || person.isFounder === true);

/**
 * Whether removing, suspending or demoting this row would leave the clinic
 * with no administrator.
 *
 * The BFF refuses it with `LAST_ADMIN`, which is the real boundary; this exists
 * so the button says so BEFORE the click rather than after it. Suspended
 * admins do not count — a suspended person cannot sign in, so they are not
 * standing between the clinic and lockout.
 */
export const isLastAdmin = (people, person) => {
  if (!holdsAdminPower(person) || person.status !== PERSON_STATUS.ACTIVE) return false;
  const admins = (people || []).filter(
    (p) => holdsAdminPower(p) && p.status === PERSON_STATUS.ACTIVE && !isJoinRequest(p),
  );
  return admins.length <= 1 && admins.some((p) => p.key === person.key);
};

/**
 * Everything the viewer is allowed to see, as one list.
 *
 * The two `can*` flags are not cosmetic: `doctors.view` and `people.view` are
 * separate catalog permissions, so a receptionist may legitimately see who the
 * clinic's practitioners are without seeing its payroll-adjacent staff list.
 * Filtering here rather than in the table keeps the KPI counts honest too — a
 * count must never include rows the viewer cannot open.
 *
 * `viewerSub` and `founderSub` decorate rather than filter: three of the page's
 * rules (§15) are about WHO a row is relative to the reader — yourself, the
 * clinic's founder, the last administrator — and each is needed in three places
 * (the table badge, the drawer alert, the disabled footer button). Deriving
 * them once here is what stops the three from disagreeing.
 */
export const buildPersonnel = ({
  links = [], staff = [], requests = [],
  canViewPractitioners = true, canViewStaff = true,
  viewerSub = '', founderSub = '',
}) => {
  const rows = [
    ...(canViewPractitioners ? links.map(practitionerToPerson) : []),
    ...(canViewStaff ? staff.map(staffToPerson) : []),
    ...(canViewStaff ? requests.map(requestToPerson) : []),
  ].map((p) => ({
    ...p,
    isSelf: !!viewerSub && p.personId === viewerSub,
    // The founder is identified by `clinics.keycloak_user_id`, not by tier: the
    // BFF stores them as `clinic_admin` and never assigns a `clinic_owner`
    // tier, so a tier check alone would let the panel demote its own owner.
    // The tier check stays as a fallback for a clinic whose data predates that.
    isFounder: p.kind === KIND.STAFF
      && ((!!founderSub && p.personId === founderSub) || p.tier === 'OWNER'),
  }));
  // Two passes: `isLastAdmin` is a fact about the whole list, so it cannot be
  // decided while the list is still being built.
  return rows.map((p) => ({
    ...p,
    isLastAdmin: isLastAdmin(rows, p),
    issues: issuesFor(p),
  }));
};

/**
 * Filter by kind, free text, org role and status.
 *
 * The role filter deliberately excludes practitioners: org roles are a staff
 * concept (a practitioner's «نقش» column shows their profession, which is
 * identity, not a clinic-authored role). Answering "who is a حسابدار" with a
 * doctor because the filter had nothing to match on would be worse than
 * answering with nothing.
 */
export const filterPersonnel = (people, { kind = 'all', q = '', roleId = '', status = '' } = {}) => {
  const needle = normText(q);
  return people.filter((p) => {
    if (kind !== 'all' && p.kind !== kind) return false;
    if (status && p.status !== status) return false;
    if (roleId) {
      if (p.kind !== KIND.STAFF) return false;
      if (!(p.roles || []).some((r) => r.id === roleId)) return false;
    }
    if (needle) {
      // Role names are searchable too: «حسابدار» in the search box is a
      // reasonable way to ask "who are the accountants", and answering it with
      // nothing while a «نقش» filter sits three controls away is a worse
      // answer than the obvious one.
      const haystack = [
        p.displayName, p.phone, p.medicalCode, p.specialty,
        ...(p.roles || []).map((r) => r.nameFa),
      ].filter(Boolean).map(normText).join(' ');
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
};

/**
 * Pending first, then by name.
 *
 * The old «پزشکان» screen expressed the same priority as a SECOND TABLE stacked
 * above the first, which is why it could not also be searched, filtered or
 * paged. Sorting says the same thing and survives all three.
 */
export const sortPersonnel = (people, sort = SORT.STATUS) => {
  // Unnamed rows (a profile still loading, or a link with no profile at all)
  // sort last rather than first, where an empty string would otherwise put them.
  const byName = (a, b) => {
    if (!a.displayName !== !b.displayName) return a.displayName ? -1 : 1;
    return a.displayName.localeCompare(b.displayName, 'fa');
  };
  const compare = {
    [SORT.NAME]: byName,
    // Newest relationship first. A request has no `joinedAt`, so its
    // `requestedAt` stands in — both answer "since when has this row existed".
    // Rows with neither sink to the bottom rather than floating to the top,
    // where an empty string compares greater than every real timestamp.
    [SORT.JOINED]: (a, b) => {
      const at = a.joinedAt || a.requestedAt || '';
      const bt = b.joinedAt || b.requestedAt || '';
      if (!at !== !bt) return at ? -1 : 1;
      if (at !== bt) return at < bt ? 1 : -1;
      return byName(a, b);
    },
    [SORT.STATUS]: (a, b) => {
      const byStatus = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
      return byStatus || byName(a, b);
    },
  }[sort] || null;
  return [...people].sort(compare || ((a, b) => {
    const byStatus = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
    return byStatus || byName(a, b);
  }));
};

/** The four KPI numbers, counted over what the viewer can actually see. */
export const personnelCounts = (people) => ({
  practitioners: people.filter((p) => p.kind === KIND.PRACTITIONER && p.status === PERSON_STATUS.ACTIVE).length,
  staff: people.filter((p) => p.kind === KIND.STAFF && p.status === PERSON_STATUS.ACTIVE).length,
  pending: people.filter((p) => p.status === PERSON_STATUS.PENDING).length,
  suspended: people.filter((p) => p.status === PERSON_STATUS.SUSPENDED).length,
  /** Active rows with something a human has to go and fix (§5.4). */
  needsAttention: people.filter((p) => actionableIssues(p).length > 0).length,
});

/**
 * Which row a URL is pointing at.
 *
 * `?person=` is a membership key and is exact. `?personId=` is a Keycloak
 * `sub`, which is what every OTHER screen in the app knows a person by —
 * reception, the cash desk and payments all hold a doctor id, none of them
 * holds a `clinic_doctors` link id — so without it those screens cannot link
 * here at all.
 *
 * A person can legitimately match twice (a practitioner who is also staff);
 * the practitioner row wins, because that is the row those screens are asking
 * about when they hand us a doctor id.
 */
export const resolvePersonParam = (people, { person = '', personId = '' } = {}) => {
  if (person && (people || []).some((p) => p.key === person)) return person;
  if (personId) {
    const matches = (people || []).filter((p) => p.personId === personId);
    const preferred = matches.find((p) => p.kind === KIND.PRACTITIONER) || matches[0];
    if (preferred) return preferred.key;
  }
  return '';
};

/**
 * The same person's row under the other kind, if the clinic has one.
 *
 * A dentist who also runs the front desk is two memberships and two rows, and
 * a manager who edits one and expects the other to change would be wrong. The
 * drawer says so and offers the crossing (§7.2).
 */
export const counterpartOf = (people, person) => {
  if (!person?.personId) return null;
  return (people || []).find(
    (p) => p.personId === person.personId && p.kind !== person.kind && !isJoinRequest(p),
  ) || null;
};

/**
 * Whether this row's actions belong to the viewer.
 *
 * Two independent grants, one per kind — and an OWNER row is read-only for
 * everyone, matching MemberDrawer's existing rule (the owner cannot be demoted
 * or removed from their own clinic through this screen).
 */
export const canManagePerson = (person, { canManageStaff, canManagePractitioners }) => {
  if (!person) return false;
  if (person.kind === KIND.PRACTITIONER) return !!canManagePractitioners;
  if (person.tier === 'OWNER' || person.isFounder) return false;
  return !!canManageStaff;
};

/**
 * The finer rules, separately for editing and for lifecycle (§7.8, §15).
 *
 * `canManagePerson` above answers a question about the READER — does my grant
 * cover this kind of row at all. This answers questions about the ROW: three
 * rules the server enforces and the old page did not mention until after the
 * click, so a manager learned them by being refused.
 *
 *   self         a non-admin cannot edit their own roles (self-escalation), and
 *                nobody suspends or removes themselves by accident
 *   founder      immutable from this panel, whatever anyone holds
 *   last admin   the clinic must keep one way back in
 *
 * `reason` is a translation KEY, not a sentence: this file has no `t` (it is
 * the layer the unit tests drive without a catalogue), and a `title` that
 * reads in Persian on an English panel would be the same defect the permission
 * labels were fixed for.
 */
export const personGuards = (person, { canManageStaff, canManagePractitioners, isAdmin = false } = {}) => {
  const base = { granted: false, canEdit: false, canLifecycle: false, editReason: '', lifecycleReason: '' };
  if (!person) return base;

  // `granted` is the coarse answer — does my grant cover this kind of row at
  // all. It is what decides whether an action area is RENDERED; the two finer
  // flags decide whether the controls in it are live. The distinction matters:
  // a blocked-by-rule button must still appear, disabled and explaining itself,
  // while a button the reader has no grant for is simply absent (§10).
  const granted = canManagePerson(person, { canManageStaff, canManagePractitioners });
  if (!granted) {
    const ownerReason = person.isFounder || person.tier === 'OWNER' ? 'people.drawer.ownerReadOnly' : '';
    return { ...base, editReason: ownerReason, lifecycleReason: ownerReason };
  }

  // Editing your own roles is self-escalation unless you are already an admin,
  // in which case there is nothing to escalate to.
  const selfEditBlocked = person.isSelf && !isAdmin;
  // Suspending or removing yourself is blocked for everyone, admin included:
  // it is the one destructive action whose victim cannot undo it.
  const selfLifecycleBlocked = person.isSelf;
  const lastAdminBlocked = person.isLastAdmin === true;

  return {
    granted: true,
    canEdit: !selfEditBlocked,
    canLifecycle: !selfLifecycleBlocked && !lastAdminBlocked,
    editReason: selfEditBlocked ? 'people.drawer.self' : '',
    lifecycleReason: lastAdminBlocked ? 'people.guard.lastAdmin'
      : selfLifecycleBlocked ? 'people.guard.self' : '',
  };
};

/**
 * Professions that carry the «دکتر» honorific.
 *
 * A clinic's کادر درمان is not all doctors — a physiotherapist, a midwife or a
 * nurse practitioner is booked exactly like a physician but is not «دکتر», and
 * prefixing them all would be wrong in the one direction that reads as
 * disrespect. So the title is DATA: a specialty is matched against these stems,
 * and adding a profession never means touching a component.
 *
 * Matched on the folded form, so «دندانپزشک» and «دندان‌پزشک» both hit, and the
 * Latin stems catch a profile written in English.
 */
// i18n-exempt: these are MATCH STEMS against stored professional data, not copy
// shown to anyone — translating them would break the match they exist to make.
const DOCTORATE_STEMS = [
  'پزشک', 'جراح', 'دندانپزشک', 'روانپزشک', 'دکتر',
  'doctor', 'physician', 'dentist', 'surgeon', 'psychiatrist', 'gp',
];

/** True when a name already carries a title, so it is never doubled. */
// i18n-exempt: prefixes DETECTED in stored names, not rendered copy.
const EXISTING_TITLES = ['دکتر', 'دکترا', 'dr', 'dr.'];

/**
 * Whether this person's name should be rendered with the doctor honorific.
 *
 * Staff never qualify — «دکتر» is a professional title, not a courtesy — and a
 * practitioner whose profile has not loaded does not either: guessing a title
 * from an id would put «دکتر» in front of a receptionist's misfiled row.
 */
export const usesDoctorTitle = (person) => {
  if (!person || person.kind !== KIND.PRACTITIONER) return false;
  const name = normText(person.displayName);
  if (EXISTING_TITLES.some((prefix) => name.startsWith(`${prefix} `) || name.startsWith(`${prefix}.`))) return false;
  const specialty = normText(person.specialty);
  if (!specialty) return false;
  return DOCTORATE_STEMS.some((stem) => specialty.includes(normText(stem)));
};

// ── Presence (ساعات حضور) ─────────────────────────────────────────────────────
//
// The DATA is owned by `scheduling` (doctor_presences, enforced at booking).
// The clinic is nonetheless the legitimate WRITER of it — a manager agreeing
// shifts with a practitioner is the whole point of the relationship — and the
// clinic BFF already routes `updateDoctorLink({ presences })` to
// `replaceDoctorPresences`. Ownership means one service holds the rows, not
// that no screen may edit them.
//
// The write is a REPLACE, not a merge: the editor always sends the whole week,
// so a removed slot is actually removed rather than resurrected on the next save.

/** GraphQL speaks Iranian weekdays, 0 = شنبه — the same index `weekdayNames()` uses. */
export const PRESENCE_DAYS = [0, 1, 2, 3, 4, 5, 6];

/** A range's service list, normalised: unique, sorted, [] = every service (PM-0468 §2.3). */
export const normalizeServiceIds = (ids) => [...new Set((ids || []).map(String).filter(Boolean))].sort();

/** Only the wire fields, ordered — what the mutation may be given. */
export const toPresenceInput = (slots) => (slots || []).map((s) => ({
  dayOfWeek: Number(s.dayOfWeek) || 0,
  startTime: s.startTime,
  endTime: s.endTime,
  serviceCatalogIds: normalizeServiceIds(s.serviceCatalogIds),
}));

/** Day, then start — a week the eye can read down rather than the insert order. */
export const sortPresence = (slots) => [...(slots || [])].sort(
  (a, b) => (a.dayOfWeek - b.dayOfWeek) || String(a.startTime).localeCompare(String(b.startTime)),
);

/**
 * What is wrong with this week, as slot indices.
 *
 * An end at or before its start is a shift that cannot be worked, and two
 * overlapping slots on one day double-book the same hour — scheduling would
 * accept both and then offer the doctor's 10:00 twice. Caught here so the
 * manager sees it on the row rather than as a booking bug next week.
 */
export const presenceProblems = (slots) => Object.keys(presenceIssues(slots)).map(Number).sort((a, b) => a - b);

export const PRESENCE_ISSUE = { ORDER: 'order', OVERLAP: 'overlap', UNKNOWN_SERVICE: 'unknown_service' };

/** ORDER and OVERLAP block a save; UNKNOWN_SERVICE is dropped on save after a confirm. */
export const BLOCKING_PRESENCE_ISSUES = [PRESENCE_ISSUE.ORDER, PRESENCE_ISSUE.OVERLAP];

/**
 * The same verdict as `presenceProblems`, keyed by slot index with the REASON,
 * so the editor can say which of the things is wrong under the row itself.
 * A backwards range is reported as ORDER even when it also overlaps something:
 * it is the fix the manager has to make first, and its overlap is meaningless.
 * Shifts that merely touch (a morning ending as the afternoon starts) are fine —
 * and two touching ranges with DIFFERENT service lists are two windows, never
 * merged (filter-then-merge, PM-0468 §2.3).
 *
 * `performedIds` — the catalogue ids this person performs — turns on the third
 * verdict: a range naming a service they no longer perform. Null = unknown =
 * not judged (a list that could not be read must not flag every row).
 */
export const presenceIssues = (slots, { performedIds = null } = {}) => {
  const list = toPresenceInput(slots);
  const out = {};
  list.forEach((s, i) => {
    if (!s.startTime || !s.endTime || s.startTime >= s.endTime) out[i] = PRESENCE_ISSUE.ORDER;
  });
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      if (list[i].dayOfWeek !== list[j].dayOfWeek) continue;
      if (list[i].startTime < list[j].endTime && list[j].startTime < list[i].endTime) {
        out[i] = out[i] || PRESENCE_ISSUE.OVERLAP;
        out[j] = out[j] || PRESENCE_ISSUE.OVERLAP;
      }
    }
  }
  if (Array.isArray(performedIds)) {
    const known = new Set(performedIds.map(String));
    list.forEach((s, i) => {
      if (!out[i] && s.serviceCatalogIds.some((id) => !known.has(id))) out[i] = PRESENCE_ISSUE.UNKNOWN_SERVICE;
    });
  }
  return out;
};

/** Strip the ids this person no longer performs; `touched` = how many rows changed. */
export const dropUnknownServices = (slots, performedIds) => {
  const known = new Set((performedIds || []).map(String));
  let touched = 0;
  const out = toPresenceInput(slots).map((s) => {
    const kept = s.serviceCatalogIds.filter((id) => known.has(id));
    if (kept.length !== s.serviceCatalogIds.length) touched += 1;
    return { ...s, serviceCatalogIds: kept };
  });
  return { slots: out, touched };
};

/**
 * «تقسیم بازه»: one range becomes two at `at` (HH:MM), both inheriting the
 * service list — the one-click way to «۸–۱۰ A، ۱۰–۱۲ B». Null when the cut is
 * not strictly inside the range.
 */
export const splitPresenceRange = (slots, index, at) => {
  const list = toPresenceInput(slots);
  const s = list[index];
  if (!s || !at || !(s.startTime < at && at < s.endTime)) return null;
  const first = { ...s, endTime: at, serviceCatalogIds: [...s.serviceCatalogIds] };
  const second = { ...s, startTime: at, serviceCatalogIds: [...s.serviceCatalogIds] };
  return [...list.slice(0, index), first, second, ...list.slice(index + 1)];
};

/**
 * The readiness strip (PM-0468 §4.2): which of the services this person
 * performs sit in NO range. Informational — a range with an empty list admits
 * every service, so one such range covers everything. An empty week answers
 * nothing: that is the empty state's message, not the strip's.
 */
export const presenceReadiness = (slots, services) => {
  const list = toPresenceInput(slots);
  if (!list.length || !Array.isArray(services)) return [];
  if (list.some((s) => s.serviceCatalogIds.length === 0)) return [];
  const covered = new Set(list.flatMap((s) => s.serviceCatalogIds));
  return services.filter((svc) => !covered.has(String(svc.catalogId)));
};

const toMinutes = (hhmm) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};
const toHHMM = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/**
 * The range «افزودن بازه» proposes for one day: the day's first range is a
 * morning, and every later one starts where the latest one ends and runs three
 * hours (capped at 23:59) — 08:00–12:00 then 12:00–15:00, the wireframe's own
 * example. It never invents an overlap; a day already worked to midnight gets
 * an empty-length range that the inline ORDER error asks the manager to fix.
 */
export const nextPresenceRange = (slots, dayOfWeek) => {
  const ends = (slots || [])
    .filter((s) => Number(s.dayOfWeek) === dayOfWeek)
    .map((s) => toMinutes(s.endTime))
    .filter((m) => m != null);
  if (ends.length === 0) return { dayOfWeek, startTime: '08:00', endTime: '12:00' };
  const start = Math.min(Math.max(...ends), 23 * 60 + 59);
  return { dayOfWeek, startTime: toHHMM(start), endTime: toHHMM(Math.min(start + 180, 23 * 60 + 59)) };
};

/**
 * Copy one day's ranges onto other days.
 *
 * `replace` drops whatever the target days had; `append` keeps it and adds the
 * copies, which may overlap — deliberately left to the validation rather than
 * silently merged, because which of two overlapping shifts is right is the
 * manager's call. Copies are appended at the end so rows already on screen keep
 * their positions.
 */
export const copyPresenceDay = (slots, fromDay, toDays, mode = 'replace') => {
  const list = toPresenceInput(slots);
  const targets = new Set((toDays || []).filter((d) => d !== fromDay));
  const source = sortPresence(list.filter((s) => s.dayOfWeek === fromDay));
  const kept = mode === 'replace' ? list.filter((s) => !targets.has(s.dayOfWeek)) : list;
  const copies = [...targets].sort((a, b) => a - b)
    .flatMap((day) => source.map((s) => ({ dayOfWeek: day, startTime: s.startTime, endTime: s.endTime })));
  return [...kept, ...copies];
};

/** Compare two weeks by value — the drawer's dirty flag, order-insensitive. */
export const samePresence = (a, b) => {
  const key = (s) => `${s.dayOfWeek}|${s.startTime}|${s.endTime}|${s.serviceCatalogIds.join(',')}`;
  const x = sortPresence(toPresenceInput(a)).map(key);
  const y = sortPresence(toPresenceInput(b)).map(key);
  return x.length === y.length && x.every((v, i) => v === y[i]);
};
